import { View, StyleSheet } from "react-native";
import { Text } from "react-native-paper";

import { AddressQuestion } from "./AddressQuestion";
import { BloodPressureQuestion } from "./BloodPressureQuestion";
import { BodyMetricsQuestion } from "./BodyMetricsQuestion";
import { CheckQuestion } from "./CheckQuestion";
import { DateQuestion, TimeQuestion } from "./DateTimeQuestion";
import { DropdownQuestion } from "./DropdownQuestion";
import { MedicationQuestion } from "./MedicationQuestion";
import { NumberQuestion } from "./NumberQuestion";
import { PhoneQuestion } from "./PhoneQuestion";
import { PhysicianQuestion } from "./PhysicianQuestion";
import { PlaceholderQuestion } from "./PlaceholderQuestion";
import { RadioQuestion } from "./RadioQuestion";
import { SignatureQuestion } from "./SignatureQuestion";
import { TextShortQuestion, TextLongQuestion } from "./TextQuestion";
import type { QuestionComponentProps } from "./types";
import { YesNoQuestion } from "./YesNoQuestion";

import type { Question } from "@/lib/api";
import { colors } from "@/lib/theme";

const COMPONENT_MAP: Record<string, React.ComponentType<QuestionComponentProps>> = {
    radio: RadioQuestion,
    check: CheckQuestion,
    yesNo: YesNoQuestion,
    textShort: TextShortQuestion,
    textLong: TextLongQuestion,
    number: NumberQuestion,
    date: DateQuestion,
    time: TimeQuestion,
    bloodPressure: BloodPressureQuestion,
    bodyMetrics: BodyMetricsQuestion,
    dropDown: DropdownQuestion,
    employeeSignature: SignatureQuestion,
    patientSignature: SignatureQuestion,
    customSignature: SignatureQuestion,
    medicationProfile: MedicationQuestion,
    patientPhysician: PhysicianQuestion,
    phone: PhoneQuestion,
    address: AddressQuestion
};

interface Props {
    question: Question;
    value: string | undefined;
    onChange: (value: string) => void;
    readOnly?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    depth?: number;
    childQuestions?: Question[];
    childAnswers?: Record<string, string>;
    onChildAnswerChange?: (questionId: string, value: string) => void;
    visitId?: string;
    sectionId?: string;
    isActive?: boolean;
    isStreaming?: boolean;
    patient?: { dob: string | null; gender: string | null };
    /**
     * When true, render ONLY the question control — no label, no hairline
     * container. Used by the flat-section QuestionsChapter where SpecRow
     * already owns the question label + numbering above the control.
     * The legacy wizard path (no longer in use after the flat redesign)
     * relied on QuestionRenderer rendering the label itself.
     */
    bare?: boolean;
}

// textShort/textLong render their own label inside the card, so we skip the
// external label render to avoid duplication. Only relevant when bare=false.
const HIDES_OWN_LABEL = new Set(["textShort", "textLong"]);

// Per-question UI overrides — render these question IDs as a NumberQuestion
// regardless of their DB type. The saved value still matches the original
// option `value` (e.g. radio Q199 has options "1"..."10" — typing 5 into
// the number field stores "5", which is identical to picking the "5" chip).
//
// Used for radio-of-integers questions whose 10-chip UI is bulky on mobile.
const RENDER_AS_NUMBER_BY_ID = new Set([
    "199", // Highest Pain Intensity (1–10 radio in DB; nicer as a numeric field)
    "6" // Pain Intensity After Intervention (0–10 radio in DB; nicer as a numeric field)
]);

export function QuestionRenderer({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    onBlur,
    depth = 0,
    childQuestions,
    childAnswers,
    onChildAnswerChange,
    visitId,
    sectionId,
    isActive,
    isStreaming,
    patient,
    bare = false
}: Props) {
    const overrideAsNumber = RENDER_AS_NUMBER_BY_ID.has(String(question.id));
    const Component = overrideAsNumber
        ? NumberQuestion
        : (COMPONENT_MAP[question.type] ?? PlaceholderQuestion);
    const hideExternalLabel = bare || HIDES_OWN_LABEL.has(question.type);

    const componentEl = (
        <Component
            question={question}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            onFocus={onFocus}
            onBlur={onBlur}
            childQuestions={childQuestions}
            childAnswers={childAnswers}
            onChildAnswerChange={onChildAnswerChange}
            visitId={visitId}
            sectionId={sectionId}
            patient={patient}
            isActive={isActive}
            isStreaming={isStreaming}
        />
    );

    if (bare) {
        return componentEl;
    }

    return (
        <View style={[styles.container, { marginLeft: depth * 16 }]}>
            {!hideExternalLabel && (
                <Text style={styles.questionText}>
                    {question.text}
                    {question.mandatory && <Text style={styles.required}> *</Text>}
                </Text>
            )}
            {componentEl}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        paddingVertical: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    questionText: {
        fontSize: 15,
        fontWeight: "500",
        marginBottom: 8,
        color: colors.textPrimary
    },
    required: {
        color: colors.error
    }
});
